import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of, timer } from 'rxjs';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { Logger } from '@app/utils/logger';
import { EVENT } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { AvatarCreateActions } from './create.actions';
import {
  selectAvatarCreateMaxPollingAttempts,
  selectAvatarCreateMaxPollingDuration,
  selectAvatarCreatePollingAttempts,
  selectAvatarCreatePollingStartTime
} from './create.selectors';
import { AvatarCreateService } from './create.service';
import { AvatarSourceType } from './create.state';

/**
 * Effects for avatar create feature.
 * Handles side effects like API calls, camera access, polling, and navigation.
 *
 * @exports
 * @class AvatarCreateEffects
 */
@Injectable()
export class AvatarCreateEffects extends AvatarCreateService {
  private readonly actions$ = inject(Actions);
  private readonly eventService = inject(EventService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly log = Logger.getInstance('AvatarCreateEffects');

  /**
   * Effect to handle avatar completion.
   * Shows success toast, publishes AVATAR_NOTIFICATION event, and navigates to home.
   */
  avatarCompleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.avatarCompleted),
        tap(() => {
          // Publish AVATAR_NOTIFICATION event to trigger cache invalidation
          const profileId = this.session.getProfile()?.id;
          this.eventService.publish(EVENT.AVATAR_NOTIFICATION, {
            __typename: 'avatar',
            type: 'AVATAR_COMPLETED',
            profileId: profileId
          });

          this.eventService.publish(
            EVENT.TOAST_SUCCESS,
            'Your avatar has been generated successfully!'
          );
        }),
        delay(1000),
        tap(() => {
          this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to handle avatar generation failure.
   * Shows error toast.
   */
  avatarFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.avatarFailed),
        tap(({ error }) => {
          this.eventService.publish(EVENT.TOAST_ERROR, {
            duration: 4000,
            message: error || 'Failed to generate avatar. Please try again.'
          });
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to open camera for photo capture.
   * Note: Camera preview is now handled in the component (create.page.ts).
   * This effect is kept for compatibility but camera preview logic has been moved to component.
   * The component handles camera preview with overlay directly.
   */
  openCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.openCamera),
        tap(() => {
          // Camera preview is now handled in the component
          // The component's openCameraPreview() method is called directly
          // This effect is a no-op to maintain action compatibility
          this.log.log('[Camera] Camera preview handled in component');
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to open gallery for photo selection.
   * Uses Capacitor Camera API to select from photo library.
   */
  openGallery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarCreateActions.openGallery),
      switchMap(() =>
        from(
          (async () => {
            try {
              this.log.log('[Gallery] Starting gallery selection...');

              // Check photo library permissions
              const permissions = await Camera.checkPermissions();
              this.log.log('[Gallery] Permissions:', permissions);

              if (permissions.photos === 'denied') {
                throw new Error(
                  'Photo library permission denied. Please enable access in settings.'
                );
              }

              // Request permission if not granted
              if (permissions.photos !== 'granted') {
                this.log.log('[Gallery] Requesting permissions...');
                const requestResult = await Camera.requestPermissions({
                  permissions: ['photos']
                });
                this.log.log(
                  '[Gallery] Permission request result:',
                  requestResult
                );

                if (requestResult.photos !== 'granted') {
                  throw new Error(
                    'Photo library permission is required to select a photo.'
                  );
                }
              }

              // Select photo from gallery
              // Use Uri instead of DataUrl for better Android compatibility
              // Plugin is pre-warmed on page load to prevent Android hangs
              this.log.log('[Gallery] Opening photo picker...');
              const image = await Camera.getPhoto({
                quality: 80,
                allowEditing: false,
                resultType: CameraResultType.Uri,
                source: CameraSource.Photos
              });
              this.log.log('[Gallery] Image selected:', {
                path: image.path,
                webPath: image.webPath,
                format: image.format
              });

              let dataUrl: string;

              // Handle web vs native platforms differently
              if (Capacitor.isNativePlatform() && image.path) {
                // Native platform: read file using Filesystem
                this.log.log(
                  '[Gallery] Reading file from native path:',
                  image.path
                );
                const fileData = await Filesystem.readFile({
                  path: image.path
                });

                const base64Data =
                  typeof fileData.data === 'string'
                    ? fileData.data
                    : await this.blobToBase64(fileData.data);

                // Determine mime type from format
                const mimeType =
                  image.format === 'png' ? 'image/png' : 'image/jpeg';
                dataUrl = `data:${mimeType};base64,${base64Data}`;
                this.log.log(
                  '[Gallery] Converted to data URL, length:',
                  dataUrl.length
                );
              } else if (image.webPath) {
                // Web platform: fetch the blob URL and convert to data URL
                this.log.log(
                  '[Gallery] Converting web path to data URL:',
                  image.webPath
                );
                dataUrl = await this.webPathToDataUrl(image.webPath);
                this.log.log(
                  '[Gallery] Converted to data URL, length:',
                  dataUrl.length
                );
              } else {
                throw new Error(
                  'Failed to select photo: No path or webPath available'
                );
              }

              this.log.log('[Gallery] Dispatching photoSelected action');
              return AvatarCreateActions.photoSelected({
                dataUrl,
                sourceType: AvatarSourceType.GALLERY
              });
            } catch (error: any) {
              this.log.error('[Gallery] Error in gallery selection:', error);

              // Handle user cancellation (don't show error message)
              const errorMessage =
                typeof error === 'string' ? error : (error?.message ?? '');
              const isCancelled =
                errorMessage.toLowerCase().includes('cancel') ??
                errorMessage.toLowerCase().includes('user') ??
                errorMessage === 'No Image Selected';

              if (isCancelled) {
                this.log.log('[Gallery] User cancelled selection');
                // User cancelled - just reset loading state without showing error
                return AvatarCreateActions.photoSelectionFailed({
                  error: ''
                });
              }

              this.log.log('[Gallery] Dispatching photoSelectionFailed action');
              return AvatarCreateActions.photoSelectionFailed({
                error: errorMessage ?? 'Failed to select photo from gallery'
              });
            }
          })()
        )
      ),
      catchError((error: any) => {
        this.log.error('[Gallery] Unhandled error in effect pipe:', error);
        // Ensure loading state is reset even if error escapes the async function
        return of(
          AvatarCreateActions.photoSelectionFailed({
            error:
              error?.message ??
              'An unexpected error occurred while selecting photo'
          })
        );
      })
    )
  );

  /**
   * Effect to handle polling timeout (10 seconds).
   * Shows background processing message and navigates to home.
   */
  pollingTimeout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.pollingTimeout),
        tap(() => {
          this.eventService.publish(EVENT.TOAST_SUCCESS, {
            color: 'toast-info',
            cssClass: 'ion-toast-text-white',
            duration: 4000,
            message:
              "Your avatar is being generated in the background. You will be notified when it's ready."
          });
        }),
        delay(1000),
        tap(() => {
          this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to poll avatar status.
   * Polls every 2-3 seconds until completion, failure, or timeout (10 seconds).
   */
  pollStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarCreateActions.startPolling),
      switchMap(() =>
        timer(0, 2500).pipe(
          // Poll every 2.5 seconds
          withLatestFrom(
            this.store.select(selectAvatarCreatePollingAttempts),
            this.store.select(selectAvatarCreatePollingStartTime),
            this.store.select(selectAvatarCreateMaxPollingDuration),
            this.store.select(selectAvatarCreateMaxPollingAttempts)
          ),
          takeWhile(
            ([, attempts, startTime, maxDuration, maxAttempts]) => {
              // Stop if max attempts reached
              if (attempts >= maxAttempts) {
                return false;
              }

              // Stop if max duration exceeded (10 seconds)
              if (startTime && Date.now() - startTime > maxDuration) {
                return false;
              }

              return true;
            },
            true // Include the last emission
          ),
          mergeMap(() =>
            this.getAvatarStatus().pipe(
              map((response) => {
                if (response.success) {
                  const { processing, current, lastFailed } = response.data;

                  // Check if processing is complete
                  if (current?.status === 'COMPLETED' && current.hasImages) {
                    return AvatarCreateActions.avatarCompleted({
                      avatarId: current.avatarId
                    });
                  }

                  // Check if processing failed
                  if (lastFailed?.status === 'FAILED') {
                    return AvatarCreateActions.avatarFailed({
                      error: lastFailed.error ?? 'Avatar generation failed'
                    });
                  }

                  // Still processing
                  if (processing?.status === 'PROCESSING') {
                    return AvatarCreateActions.pollStatusSuccess({
                      status: processing.status,
                      data: response.data
                    });
                  }
                }

                return AvatarCreateActions.pollStatusFailed({
                  error: 'Invalid status response'
                });
              }),
              catchError((error) =>
                of(
                  AvatarCreateActions.pollStatusFailed({
                    error: error.message ?? 'Failed to poll status'
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  /**
   * Effect to handle polling success.
   * Continues polling by dispatching pollStatus action.
   */
  pollStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarCreateActions.pollStatusSuccess),
      withLatestFrom(
        this.store.select(selectAvatarCreatePollingAttempts),
        this.store.select(selectAvatarCreatePollingStartTime),
        this.store.select(selectAvatarCreateMaxPollingDuration),
        this.store.select(selectAvatarCreateMaxPollingAttempts)
      ),
      switchMap(([, attempts, startTime, maxDuration, maxAttempts]) => {
        // Check if we should stop polling
        if (attempts >= maxAttempts) {
          return of(AvatarCreateActions.pollingMaxAttemptsReached());
        }

        if (startTime && Date.now() - startTime > maxDuration) {
          return of(AvatarCreateActions.pollingTimeout());
        }

        // Continue polling
        return of(AvatarCreateActions.pollStatus());
      })
    )
  );

  /**
   * Effect to pre-warm Camera plugin on flow initialization.
   * This helps prevent the Android issue where the first Camera.getPhoto() call hangs.
   * By calling checkPermissions() early, we "warm up" the plugin so the first real call works.
   */
  preWarmCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.initializeFlow),
        switchMap(() =>
          from(
            (async () => {
              try {
                this.log.log('[Gallery] Pre-warming Camera plugin...');
                // Pre-warm by checking permissions - this initializes the plugin
                // without showing any dialogs or requiring user interaction
                const permissions = await Camera.checkPermissions();
                this.log.log(
                  '[Gallery] Pre-warm complete, permissions:',
                  permissions
                );

                // Optionally request permissions if not granted (but don't fail if denied)
                // This ensures permissions are ready when user clicks the button
                if (
                  permissions.photos !== 'granted' &&
                  permissions.photos !== 'denied'
                ) {
                  this.log.log(
                    '[Gallery] Requesting permissions during pre-warm...'
                  );
                  const requestResult = await Camera.requestPermissions({
                    permissions: ['photos']
                  });
                  this.log.log(
                    '[Gallery] Pre-warm permission request result:',
                    requestResult
                  );
                }
              } catch (error: any) {
                // Don't fail the flow if pre-warm fails - it's just an optimization
                this.log.warn(
                  '[Gallery] Pre-warm failed (non-critical):',
                  error?.message
                );
              }
            })()
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * Effect to upload avatar image to server.
   * Converts base64 to FormData and uploads via multipart/form-data.
   */
  uploadAvatar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AvatarCreateActions.uploadAvatar),
      withLatestFrom(this.store),
      switchMap(([, state]) => {
        const selectedImage = state.avatarCreate.selectedImage;

        if (!selectedImage.dataUrl || !selectedImage.sourceType) {
          return of(
            AvatarCreateActions.uploadAvatarFailed({
              error: 'No image selected'
            })
          );
        }

        // Convert data URL to Blob
        const base64Data = selectedImage.dataUrl.split(',')[1];
        const mimeType = selectedImage.dataUrl
          .split(',')[0]
          .split(':')[1]
          .split(';')[0];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        // Create FormData
        const formData = new FormData();
        formData.append('sourceType', selectedImage.sourceType);
        formData.append('sourceImage', blob, 'avatar.jpg');

        return this.uploadAvatar(formData, true).pipe(
          map((response) => {
            if (response.success) {
              return AvatarCreateActions.uploadAvatarSuccess({
                avatarId: response.data.avatarId,
                estimatedTime: response.data.estimatedTime
              });
            }
            return AvatarCreateActions.uploadAvatarFailed({
              error: 'Upload failed'
            });
          }),
          catchError((error) => {
            // Handle validation errors (400)
            if (error.status === 400 && error.error?.error) {
              const errorCode = error.error.error.code;
              let errorMessage = error.error.error.message;

              // Validation errors should show on confirmation page
              if (
                errorCode === 'NO_FACE_DETECTED' ||
                errorCode === 'IMAGE_TOO_DARK' ||
                errorCode === 'NEEDS_HALF_BODY'
              ) {
                const errorCodeMap: Record<string, string> = {
                  NO_FACE_DETECTED: 'No face detected',
                  IMAGE_TOO_DARK: 'The image is too dark',
                  NEEDS_HALF_BODY: 'Please provide a half-body selfie.'
                };
                errorMessage = errorCodeMap[errorCode] || errorMessage;
                return of(
                  AvatarCreateActions.validationError({ error: errorMessage })
                );
              }
            }

            // Handle network disconnection
            if (!navigator.onLine || error.status === 0) {
              this.log.error('[UploadAvatar] Network disconnected');

              // check avatar status to see if upload actually succeeded
              return of(true).pipe(
                delay(5000),
                switchMap(() =>
                  this.getAvatarStatus().pipe(
                    map((statusResponse) => {
                      if (statusResponse.success) {
                        const { processing, current } = statusResponse.data;

                        // If processing or current avatar exists, consider upload successful
                        if (
                          processing?.status === 'PROCESSING' ||
                          (current?.status === 'COMPLETED' && current.hasImages)
                        ) {
                          return AvatarCreateActions.uploadAvatarSuccess({
                            avatarId:
                              processing?.avatarId ?? current?.avatarId ?? ''
                          });
                        }
                      }
                      // Otherwise, treat as upload failure
                      return AvatarCreateActions.uploadAvatarFailed({
                        error:
                          error.error?.error?.message ??
                          'Failed to upload avatar'
                      });
                    }),
                    catchError(() =>
                      of(
                        AvatarCreateActions.uploadAvatarFailed({
                          error:
                            error.error?.error?.message ??
                            'Failed to upload avatar'
                        })
                      )
                    )
                  )
                )
              );
            }

            return of(
              AvatarCreateActions.uploadAvatarFailed({
                error: error.error?.error?.message ?? 'Failed to upload avatar'
              })
            );
          })
        );
      })
    )
  );

  /**
   * Effect to handle successful upload.
   * Shows toast, publishes AVATAR_NOTIFICATION event, and navigates to home immediately.
   */
  uploadAvatarSuccessNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.uploadAvatarSuccess),
        tap(() => {
          // Publish AVATAR_NOTIFICATION event to trigger cache invalidation
          const profileId = this.session.getProfile()?.id;
          this.eventService.publish(EVENT.AVATAR_NOTIFICATION, {
            __typename: 'avatar',
            type: 'AVATAR_COMPLETED',
            profileId: profileId
          });

          this.eventService.publish(EVENT.TOAST_SUCCESS, {
            duration: 4000,
            color: 'toast-info',
            cssClass: 'ion-toast-text-white',
            message:
              "Your avatar is being generated. You will be notified when it's ready."
          });
        }),
        delay(500),
        tap(() => {
          this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect to show error toast for upload failures.
   */
  uploadFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AvatarCreateActions.uploadAvatarFailed),
        tap(({ error }) => {
          this.eventService.publish(EVENT.TOAST_ERROR, {
            duration: 3000,
            color: 'toast-failed',
            cssClass: 'ion-toast-text-white',
            message: error || 'Failed to upload avatar'
          });
        })
      ),
    { dispatch: false }
  );

  /**
   * Helper to convert Blob to base64 string.
   *
   * @param {Blob} blob Blob to convert.
   * @returns {Promise<string>} Base64 string.
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      /**
       * Called when the reading operation is successfully completed.
       */
      reader.onloadend = () => {
        const result = reader.result as string;
        // Remove data URL prefix if present
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Helper to convert web blob URL to data URL.
   *
   * @param {string} webPath Web blob URL.
   * @returns {Promise<string>} Data URL.
   */
  private async webPathToDataUrl(webPath: string): Promise<string> {
    const response = await fetch(webPath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      /**
       * Called when the reading operation is successfully completed.
       */
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
